import { useState, useEffect } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Copy, ExternalLink, RefreshCw, Save, QrCode, CalendarDays, X } from "lucide-react";
import { api, errMsg } from "@/lib/galleryApi";
import { ACCESS_MODES } from "@/components/gallery/accessModes";

const PERMS = [
  ["allow_share", "Partage", "Boutons de partage du lien sur la galerie client"],
  ["allow_download_single", "Telechargement individuel", "Telecharger une photo ou une selection"],
  ["allow_download_zip", "Telechargement complet (ZIP)", "Telecharger toute la galerie en une archive"],
];

function Section({ title, children }) {
  return (
    <section className="bg-[#0B1120] border border-[#1A2332] rounded-xl p-5">
      <h3 className="text-xs uppercase tracking-[0.2em] text-[#64748B] font-semibold mb-4">{title}</h3>
      {children}
    </section>
  );
}

export function SharePanel({ gallery, onSaved }) {
  const [form, setForm] = useState({});
  const [events, setEvents] = useState([]);
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const url = `${window.location.origin}${gallery.share_url}`;

  useEffect(() => {
    setForm({
      access_mode: gallery.access_mode, is_active: gallery.is_active, strip_metadata: !!gallery.strip_metadata, event_id: gallery.event_id || "",
      permissions: { ...gallery.permissions }, expiration_date: gallery.expiration_date ? gallery.expiration_date.slice(0, 10) : "", access_code: "",
    });
  }, [gallery]);
  useEffect(() => { api.get("/event/events").then((d) => setEvents((Array.isArray(d) ? d : []).filter((e) => e.org_id === gallery.org_id))).catch(() => {}); }, [gallery.org_id]);
  useEffect(() => { QRCode.toDataURL(url, { margin: 1, width: 220 }).then(setQr); }, [url]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, event_id: form.event_id || null, expiration_date: form.expiration_date ? `${form.expiration_date}T23:59:59+00:00` : null };
      if (!body.access_code) delete body.access_code;
      const g = await api.patch(`/galleries/${gallery.id}`, body);
      onSaved(g);
      toast.success("Confidentialite et partage enregistres");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const [confirmRegen, setConfirmRegen] = useState(false);
  const regenerate = async () => {
    setConfirmRegen(false);
    const r = await api.post(`/galleries/${gallery.id}/regenerate-link`, {});
    onSaved({ ...gallery, ...r });
    toast.success("Nouveau lien genere — l'ancien lien ne fonctionne plus");
  };

  if (!form.permissions) return null;
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-5" data-testid="share-panel">
      <div className="space-y-5">
        <Section title="Mode d'acces">
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.entries(ACCESS_MODES).map(([k, m]) => {
              const Icon = m.icon;
              const active = form.access_mode === k;
              return (
                <button key={k} onClick={() => set("access_mode", k)} data-testid={`access-mode-${k}`}
                  className={`text-left rounded-lg border p-4 transition-colors ${active ? "border-[#0099CC] bg-[#0099CC]/10" : "border-[#1A2332] hover:border-[#2A3441]"}`}>
                  <div className="flex items-center gap-2 text-sm font-semibold text-white"><Icon className="w-4 h-4" style={{ color: m.color }} />{m.label}</div>
                  <p className="text-xs text-[#64748B] mt-1">{m.hint}</p>
                </button>
              );
            })}
          </div>
          {form.access_mode === "code" && (
            <div className="mt-4">
              <label className="text-xs text-[#94A3B8] mb-1.5 block">{gallery.has_access_code ? "Nouveau code d'acces (laisser vide pour conserver l'actuel)" : "Code d'acces (4 caracteres minimum)"}</label>
              <Input value={form.access_code} onChange={(e) => set("access_code", e.target.value)} placeholder="Ex. GALA2026" className="bg-[#060B14] border-[#1A2332] text-white max-w-xs" data-testid="access-code-input" />
            </div>
          )}
          {form.access_mode === "participants" && (
            <div className="mt-4">
              <label className="text-xs text-[#94A3B8] mb-1.5 block">Evenement dont les inscrits ont acces</label>
              <select value={form.event_id} onChange={(e) => set("event_id", e.target.value)} className="w-full max-w-md bg-[#060B14] border border-[#1A2332] text-white rounded-md px-3 h-10 text-sm" data-testid="participants-event-select">
                <option value="">Choisir un evenement</option>
                {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
              </select>
              <p className="text-[11px] text-[#64748B] mt-2">Le participant saisit son e-mail d'inscription et recoit un code a usage unique valable 10 minutes.</p>
            </div>
          )}
        </Section>
        <Section title="Permissions">
          <div className="space-y-4">
            {PERMS.map(([k, label, hint]) => (
              <div key={k} className="flex items-center justify-between gap-4">
                <div><p className="text-sm text-white">{label}</p><p className="text-xs text-[#64748B]">{hint}</p></div>
                <Switch checked={!!form.permissions[k]} onCheckedChange={(v) => set("permissions", { ...form.permissions, [k]: v })} data-testid={`perm-${k}`} />
              </div>
            ))}
          </div>
        </Section>
        <Section title="Metadonnees des fichiers publics">
          <div className="flex items-center justify-between gap-4">
            <div><p className="text-sm text-white">Supprimer toutes les metadonnees</p><p className="text-xs text-[#64748B]">Par defaut, la position GPS est toujours retiree ; la date, l'appareil et les reglages photo sont conserves. Les originaux restent intacts.</p></div>
            <Switch checked={!!form.strip_metadata} onCheckedChange={(v) => set("strip_metadata", v)} data-testid="strip-metadata-switch" />
          </div>
        </Section>
        <Section title="Disponibilite">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <label className="flex items-center gap-3 text-sm text-white"><Switch checked={!!form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="gallery-active-switch" />Galerie active</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#94A3B8]">Expire le</span>
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="bg-[#060B14] border-[#1A2332] text-white w-48 justify-start font-normal" data-testid="gallery-expiration-input">
                    <CalendarDays className="w-4 h-4 mr-2 text-[#64748B]" />{form.expiration_date ? format(new Date(`${form.expiration_date}T12:00:00`), "d MMMM yyyy", { locale: fr }) : "Jamais"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#0B1120] border-[#1A2332] text-white" align="start">
                  <Calendar mode="single" locale={fr} selected={form.expiration_date ? new Date(`${form.expiration_date}T12:00:00`) : undefined}
                    onSelect={(d) => { set("expiration_date", d ? format(d, "yyyy-MM-dd") : ""); setCalOpen(false); }} disabled={{ before: new Date() }} />
                </PopoverContent>
              </Popover>
              {form.expiration_date && <button onClick={() => set("expiration_date", "")} className="text-[#64748B] hover:text-white" data-testid="clear-expiration"><X className="w-4 h-4" /></button>}
            </div>
          </div>
        </Section>
        <Button onClick={save} disabled={busy} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="save-share-settings"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
      </div>
      <Section title="Lien de partage">
        {gallery.access_mode === "private" ? (
          <p className="text-sm text-[#94A3B8]" data-testid="share-private-note">La galerie est privee : le lien ne donne acces a rien. Choisissez un mode de partage puis enregistrez.</p>
        ) : (
          <div className="space-y-3">
            {qr && <img src={qr} alt="QR code de la galerie" className="w-44 h-44 rounded-lg bg-white p-2 mx-auto" data-testid="share-qr" />}
            <div className="flex items-center gap-1 bg-[#060B14] border border-[#1A2332] rounded-md px-2 py-2">
              <span className="text-xs text-[#94A3B8] truncate flex-1" data-testid="share-url">{url}</span>
              <Button size="sm" variant="ghost" className="h-7 text-[#0099CC]" onClick={() => { navigator.clipboard.writeText(url); toast.success("Lien copie"); }} data-testid="copy-share-link"><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="bg-transparent border-[#1A2332] text-[#94A3B8]" onClick={() => window.open(url, "_blank")} data-testid="open-public-gallery"><ExternalLink className="w-3.5 h-3.5 mr-1" />Voir cote client</Button>
              {qr && <Button size="sm" variant="outline" className="bg-transparent border-[#1A2332] text-[#94A3B8]" onClick={() => { const a = document.createElement("a"); a.href = qr; a.download = "qr-galerie.png"; a.click(); }}><QrCode className="w-3.5 h-3.5 mr-1" />QR</Button>}
              <Button size="sm" variant="outline" className="bg-transparent border-[#1A2332] text-[#94A3B8]" onClick={() => setConfirmRegen(true)} data-testid="regenerate-link"><RefreshCw className="w-3.5 h-3.5 mr-1" />Nouveau lien</Button>
            </div>
            <p className="text-[11px] text-[#64748B]">Seules les photos publiees et non rejetees sont visibles par les clients.</p>
            <AlertDialog open={confirmRegen} onOpenChange={setConfirmRegen}>
              <AlertDialogContent className="bg-[#0B1120] border-[#1A2332] text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Generer un nouveau lien ?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#94A3B8]">L'ancien lien, les liens deja envoyes aux clients et les telechargements en cours cesseront immediatement de fonctionner.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-[#1A2332] text-[#94A3B8]">Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={regenerate} className="bg-red-600 hover:bg-red-700" data-testid="confirm-regenerate-link">Revoquer et generer</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </Section>
    </div>
  );
}
